import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Code, StopCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { differenceInDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AutoplayABTestConfig, AutoplayVariation } from '@/store/videoFeatures/types';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { fetchVideoInformations } from '@/utils/generateVideoEmbedCode';

interface ABTestDetailsInlineProps {
  testConfig: AutoplayABTestConfig;
  variations: AutoplayVariation[];
  onViewMetrics?: () => void;
  onFinalize: () => void;
  videoId?: string;
  videoWidth?: number;
  videoHeight?: number;
}

export const ABTestDetailsInline: React.FC<ABTestDetailsInlineProps> = ({
  testConfig,
  variations,
  onViewMetrics,
  onFinalize,
  videoId,
  videoWidth: propVideoWidth,
  videoHeight: propVideoHeight
}) => {
  useLanguage();
  const { user } = useAuth();
  const [videoWidth, setVideoWidth] = useState<number | undefined>(propVideoWidth);
  const [videoHeight, setVideoHeight] = useState<number | undefined>(propVideoHeight);
  const [showCopyCodeModal, setShowCopyCodeModal] = useState(false);

  // Buscar dimensões do vídeo se não forem fornecidas como props
  useEffect(() => {
    const loadVideoDimensions = async () => {
      if (propVideoWidth && propVideoHeight) {
        setVideoWidth(propVideoWidth);
        setVideoHeight(propVideoHeight);
        return;
      }

      if (!videoId || !user?.id) return;

      try {
        const videoInfo = await fetchVideoInformations(user.id, videoId);
        if (videoInfo) {
          setVideoWidth(Number(videoInfo.videoWidth) || 1920);
          setVideoHeight(Number(videoInfo.videoHeight) || 1080);
        }
      } catch (error) {
        console.error('Erro ao buscar dimensões do vídeo:', error);
        // Usar valores padrão 16:9
        setVideoWidth(1920);
        setVideoHeight(1080);
      }
    };

    loadVideoDimensions();
  }, [videoId, user?.id, propVideoWidth, propVideoHeight]);

  const startDate = testConfig.startDate ? new Date(testConfig.startDate) : new Date();
  const endDate = testConfig.endDate ? new Date(testConfig.endDate) : null;

  const totalDays = endDate ? differenceInDays(endDate, startDate) : 14;
  const daysElapsed = differenceInDays(new Date(), startDate);
  const daysRemaining = endDate ? Math.max(0, differenceInDays(endDate, new Date())) : null;
  const progressPercent = Math.min(100, Math.max(0, (daysElapsed / totalDays) * 100));

  const handleFinalizeTest = () => {
    // Apenas abre o modal de confirmação
    onFinalize();
  };

  // Calcular porcentagem por variação
  const variationPercentage = variations.length > 0 ? Math.floor(100 / variations.length) : 0;
  const remainder = 100 - (variationPercentage * variations.length);

  return (
    <div className="space-y-6 pt-4 border-t border-border/30">
      {/* Variações em teste - Refinado */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {translateInterfaceText("Variações em teste")}</h4>
        <div className="space-y-2">
          {variations.map((variation, index) => (
            <div 
              key={variation.id}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-sm group"
            >
                {/* Status dot azul */}
                <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
              
              {/* Label da variação */}
              <span className="font-semibold text-foreground">
                {translateInterfaceText("Variação")}{" "}{String.fromCharCode(65 + index)}
              </span>
              
              <span className="text-muted-foreground/50">—</span>
              
              <span className="text-muted-foreground truncate flex-1">
                {variation.name || translateInterfaceText("Variação {value0}", { value0: (index + 1) })}
              </span>
              
              {/* Badge "Principal" para primeira variação (controle) */}
              {index === 0 && (
                <Badge 
                  variant="outline" 
                  className="text-[12px] px-2.5 py-1 h-auto bg-white text-[#6B7280] border-[#E5E7EB] font-medium rounded-full"
                >
                  {translateInterfaceText("Principal")}</Badge>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Distribuição de tráfego - Premium */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {translateInterfaceText("Distribuição de tráfego")}</h4>
          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-md">
            {translateInterfaceText("Total: 100%")}</span>
        </div>
        
        <div className="space-y-3">
          {variations.map((variation, index) => {
            const percent = index === variations.length - 1 
              ? variationPercentage + remainder 
              : variationPercentage;
            
            return (
              <div key={variation.id} className="space-y-1.5">
                {/* Nome da variação acima da barra */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {translateInterfaceText("Variação")}{" "}{String.fromCharCode(65 + index)}
                  </span>
                  {/* Percentual grande à direita */}
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    {percent}%
                  </span>
                </div>
                
                {/* Barra premium com shimmer e thumb */}
                <div className="relative h-1 bg-border/50 rounded-full overflow-visible group cursor-pointer">
                  {/* Barra preenchida com shimmer animado */}
                  <div 
                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary via-primary-light to-primary bg-[length:200%_100%] animate-shimmer-slow transition-all duration-300"
                    style={{ width: `${percent}%` }}
                  />
                  
                  {/* Thumb circular na ponta da barra */}
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-primary shadow-sm transition-all duration-150 group-hover:scale-110 group-hover:shadow-[0_0_0_4px_hsl(217_91%_60%_/_0.15)]"
                    style={{ left: `calc(${percent}% - 6px)` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progresso do teste - Linha do tempo */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {translateInterfaceText("Progresso do teste")}</h4>
        
        <div className="p-4 rounded-xl bg-muted/30 border border-border/30 space-y-4">
          {/* Header com badge dias restantes */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-foreground font-medium">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span>{translateInterfaceText("Em execução")}</span>
            </div>
            {daysRemaining !== null && (
              <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs animate-pulse-alert">
                {daysRemaining} {" "}{translateInterfaceText("dias restantes")}</Badge>
            )}
          </div>
          
          {/* Barra de progresso principal */}
          <div className="space-y-2">
            <Progress value={progressPercent} className="h-2" animationType="flow" />
            
            {/* Labels da linha do tempo */}
            <div className="flex justify-between items-center text-xs">
              <div className="flex flex-col">
                <span className="text-muted-foreground">{translateInterfaceText("Início")}</span>
                <span className="font-medium text-foreground">
                  {format(startDate, 'dd/MM', { locale: getInterfaceDateLocale() })}
                </span>
              </div>
              
              {/* Progresso percentual no meio */}
              <span className="text-sm font-semibold text-primary">
                {Math.round(progressPercent)}{translateInterfaceText("% concluído")}</span>
              
              {endDate && (
                <div className="flex flex-col items-end">
                  <span className="text-muted-foreground">{translateInterfaceText("Término")}</span>
                  <span className="font-medium text-foreground">
                    {format(endDate, 'dd/MM', { locale: getInterfaceDateLocale() })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ações - CTA refinado */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border/20">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => setShowCopyCodeModal(true)}
                className="gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                size="sm"
                style={{
                  background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(45, 108, 246, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(45, 108, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 108, 246, 0.3)';
                }}
              >
                <Code className="h-[18px] w-[18px]" />
                {translateInterfaceText("Copiar código")}</Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="center"
              className="bg-gray-900 text-white text-xs px-3 py-1.5"
            >
              {translateInterfaceText("Copiar código de incorporação do vídeo")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleFinalizeTest}
                className="text-xs gap-1.5"
              >
                <StopCircle className="h-3.5 w-3.5" />
                {translateInterfaceText("Finalizar teste")}</Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[200px] text-center">
              <p>{translateInterfaceText("Encerra o teste e deleta a configuração de A/B test.")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Modal de Copiar Código — usa videoOrigin=AUTOPLAYTEST */}
      {videoId && (
        <CopyCodeModal
          isOpen={showCopyCodeModal}
          onClose={() => setShowCopyCodeModal(false)}
          entityType="video"
          entityId={videoId}
          entityName="Teste de Autoplay"
          userId={user?.id || user?.id_user}
          videoOrigin="AUTOPLAYTEST"
          hideJsTab={true}
        />
      )}
    </div>
  );
};
